import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import { Plane, Hotel, Bus, Utensils, Phone, Stamp, ChevronRight, Check, Star } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import GeometricPattern from '../components/GeometricPattern';
import CrescentMoon from '../components/CrescentMoon';
import { COMPANY, PACKAGES as mockPackages, SERVICES, TESTIMONIALS, GALLERY_IMAGES, HERO_IMAGE, CTA_IMAGE } from '../data/mock';
import { brandConfig } from '../brandConfig';
import { SchemaMarkup } from '../components/SEO/SchemaMarkup';
import { ENTITY } from '../data/entityData';
import { ServiceChunkBlock } from '../components/AIO/ServiceChunkBlock';
import { sacredRise, fadeSacred, bloom, stagger, imageReveal, goldLine } from '../lib/animations';

const iconMap = { Plane, Hotel, Bus, Utensils, Phone, Stamp, Building: Hotel };

export default function Home() {
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  const [cms, setCms] = useState({
    homeText: "Complete Umrah packages from Addis Ababa. Flight tickets, visas, comfortable hotels, transport and meals — all arranged with care so you can focus on your Ibadah.",
    hotelText: "",
    packagesText: "From Addis Ababa to the Holy Land — everything arranged with care, so you can focus on your Ibadah."
  });

  useEffect(() => {
    let unsubs = [];
    
    if (window.listenToPackages) {
      unsubs.push(window.listenToPackages((data) => setPackages(data)));
    } else {
      setPackages(mockPackages);
    }

    if (window.listenToTestimonials) {
      unsubs.push(window.listenToTestimonials((data) => {
        if (data.length > 0) setTestimonials(data);
      }));
    }

    if (window.listenToCMS) {
      unsubs.push(window.listenToCMS((data) => {
        if (data) {
          setCms(prev => ({ ...prev, ...data }));
        }
      }));
    }

    return () => {
      unsubs.forEach(unsub => unsub && unsub());
    };
  }, []);

  return (
    <PageWrapper>
      <SchemaMarkup
        pageTitle={`${brandConfig.companyName} | Umrah & Hajj Packages from Addis Ababa`}
        pageDescription={`${brandConfig.companyName} offers complete Umrah and Hajj packages from Addis Ababa, Ethiopia. We provide visa processing, premium hotels in Mecca, and 24/7 support.`}
        pageUrl="/"
        faqItems={ENTITY.pageFAQ?.home}
      />
      {/* HERO */}
      <section style={{ position: 'relative', minHeight: '100svh', overflow: 'hidden', background: '#0A1628' }}>
        <div className="ambient-glow-left" />
        <div className="ambient-glow-right" />
        <img src={HERO_IMAGE} alt="Masjid al-Haram" style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', objectPosition: 'center',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(10,22,40,0.96) 0%, rgba(10,22,40,0.82) 45%, rgba(10,22,40,0.45) 100%)',
        }} />
        <GeometricPattern color="#C9A84C" opacity={0.08} size={100} />

        <div style={{
          position: 'relative', zIndex: 10, maxWidth: 'var(--container)',
          margin: '0 auto', padding: '120px var(--gutter) 80px',
          minHeight: '100svh', display: 'flex', alignItems: 'center',
        }}>
          <div style={{ maxWidth: 750 }}>
            <motion.div className="arabic-text" initial="hidden" animate="visible" variants={fadeSacred} custom={1}
              style={{ fontSize: 24, color: 'rgba(201,168,76,0.95)', letterSpacing: '0.08em', marginBottom: 24, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
              {COMPANY.bismillah}
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={sacredRise} custom={2}
              style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
              <div style={{ width: 48, height: 2, background: '#C9A84C', boxShadow: '0 0 10px rgba(201,168,76,0.5)' }} />
              <span className="premium-glass" style={{
                fontFamily: 'Inter', fontWeight: 600, color: '#C9A84C', fontSize: 11,
                letterSpacing: '0.3em', textTransform: 'uppercase', padding: '6px 18px', borderRadius: 20
              }}>
                Addis Ababa · Ethiopia · Open 24/7
              </span>
            </motion.div>

            <motion.h1 initial="hidden" animate="visible" variants={sacredRise} custom={3}
              style={{
                fontFamily: 'Amiri, serif', fontWeight: 700, fontSize: 'var(--display)',
                lineHeight: 0.98, letterSpacing: '-0.01em', margin: 0, color: '#FFFFFF',
              }}>
              Your Journey<br />
              <span className="gold-shimmer">to the Sacred.</span>
            </motion.h1>

            <motion.div className="arabic-text" initial="hidden" animate="visible" variants={fadeSacred} custom={6}
              style={{
                fontStyle: 'italic', color: 'rgba(251,248,240,0.9)',
                fontSize: 'var(--arabic)', marginTop: 24, textShadow: '0 2px 8px rgba(0,0,0,0.4)'
              }}>
              رحلتك المقدسة — نحن نتكفل بكل شيء
            </motion.div>

            <motion.p initial="hidden" animate="visible" variants={sacredRise} custom={8}
              style={{
                fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)',
                lineHeight: 1.85, maxWidth: 520, marginTop: 26, opacity: 0.95
              }}>
              {cms.homeText}
            </motion.p>

            <motion.div initial="hidden" animate="visible" variants={sacredRise} custom={11}
              style={{ marginTop: 42, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <Link to="/packages" style={{ textDecoration: 'none' }}>
                <button className="gold-pulse btn-premium" style={{
                  fontFamily: 'Amiri', fontWeight: 700, fontSize: 16, letterSpacing: '0.05em',
                  padding: '16px 40px', background: '#C9A84C', color: '#0A1628',
                  border: 'none', cursor: 'pointer', borderRadius: 9999
                }}>
                  View Umrah Packages
                </button>
              </Link>
              <a href={`tel:${COMPANY.phoneIntl}`} style={{ textDecoration: 'none' }}>
                <button className="btn-premium" style={{
                  fontFamily: 'Inter', fontWeight: 500, fontSize: 14, letterSpacing: '0.05em',
                  padding: '16px 32px', background: 'rgba(201,168,76,0.1)', color: '#FBF8F0',
                  border: '2px solid rgba(201,168,76,0.5)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8, borderRadius: 9999
                }}>
                  <Phone size={16} /> Call {COMPANY.phoneDisplay}
                </button>
              </a>
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={sacredRise} custom={15}
              style={{ marginTop: 54, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              {['Umrah Visa', 'Umrah Packages', 'Flight Tickets', 'Visa Services', 'Hotel Booking'].map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#FBF8F0', fontSize: 13, fontFamily: 'Inter', fontWeight: 500, letterSpacing: '0.04em' }}>
                  <Check size={16} color="#C9A84C" style={{ filter: 'drop-shadow(0 2px 4px rgba(201,168,76,0.4))' }} /> {t}
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <div style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 10, textAlign: 'center' }}>
          <div className="floating-moon">
            <CrescentMoon size={28} color="#C9A84C" animate={false} />
          </div>
          <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#C9A84C', fontSize: 11, marginTop: 10, letterSpacing: '0.35em' }}>
            SCROLL
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section style={{ background: '#FBF8F0', padding: 'var(--section) 0' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <CrescentMoon size={40} color="#C9A84C" animate />
          </div>
          <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#1B6B3A', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase' }}>
            — Our Packages —
          </div>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sacredRise}
            style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 'var(--h1)', marginTop: 16, lineHeight: 1.1 }}>
            Blessed Journeys,<br /><span style={{ color: '#1B6B3A' }}>Complete Packages.</span>
          </motion.h2>
          <p style={{ fontFamily: 'Inter', color: '#0A1628', fontSize: 'var(--body-lg)', maxWidth: 520, margin: '16px auto 0', lineHeight: 1.85 }}>
            {cms.packagesText}
          </p>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={goldLine}
            style={{ width: 80, height: 1, background: '#C9A84C', margin: '40px auto' }} />
        </div>

        <div style={{ maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}
            variants={stagger(0.15)}
            className="package-grid"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28 }}>
            {packages.slice(0, 3).map((p) => (
              <motion.div key={p.id} variants={sacredRise} className="pkg-card" style={{
                background: '#FFFFFF', border: '1px solid rgba(201,168,76,0.2)',
                borderTop: '4px solid #1B6B3A', overflow: 'hidden',
                transition: 'all 0.3s', cursor: 'pointer',
              }}>
                <div style={{ overflow: 'hidden', aspectRatio: '16/9' }}>
                  <img src={p.image} alt={p.name} style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    transition: 'transform 0.7s ease',
                  }} className="pkg-img" />
                </div>
                <div style={{ padding: '28px 24px' }}>
                  <div style={{
                    display: 'inline-block', marginBottom: 14,
                    background: 'rgba(27,107,58,0.1)', border: '1px solid rgba(27,107,58,0.3)',
                    padding: '4px 14px', fontFamily: 'Inter', fontWeight: 600,
                    color: '#1B6B3A', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase',
                  }}>
                    {p.type} · {p.duration}
                  </div>
                  <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 24 }}>
                    {p.name}
                  </div>
                  <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 16, marginTop: 4 }}>
                    {p.arabic}
                  </div>
                  <div style={{ fontFamily: 'Inter', color: '#0A1628', fontSize: 14, lineHeight: 1.75, marginTop: 14 }}>
                    {p.description}
                  </div>
                  <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#1B6B3A', fontSize: 22, marginTop: 20 }}>
                    {p.price}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 16 }}>
                    {p.includes.map((inc) => (
                      <span key={inc} style={{
                        fontFamily: 'Inter', fontWeight: 500, color: '#0A1628', fontSize: 10,
                        background: '#F5EFE0', padding: '4px 10px', letterSpacing: '0.06em',
                      }}>{inc}</span>
                    ))}
                  </div>
                  <Link to="/packages" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 22,
                    fontFamily: 'Inter', fontWeight: 600, color: '#C9A84C', fontSize: 13,
                    letterSpacing: '0.06em', textDecoration: 'none',
                    borderBottom: '1px solid transparent',
                  }} onMouseEnter={(e) => (e.currentTarget.style.borderBottom = '1px solid #C9A84C')}
                     onMouseLeave={(e) => (e.currentTarget.style.borderBottom = '1px solid transparent')}>
                    Book This Package <ChevronRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/packages" style={{
              fontFamily: 'Amiri', fontWeight: 700, color: '#0A1628', fontSize: 16,
              textDecoration: 'none', borderBottom: '2px solid #C9A84C', paddingBottom: 4,
            }}>
              View All Packages →
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section style={{ background: '#0A1628', padding: 'var(--section) 0', position: 'relative', overflow: 'hidden' }}>
        <GeometricPattern color="#C9A84C" opacity={0.05} size={120} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#C9A84C', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase' }}>
            — What We Offer —
          </div>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sacredRise}
            style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 'var(--h1)', marginTop: 16, lineHeight: 1.1 }}>
            Complete Travel<br /><span style={{ color: '#C9A84C' }}>Solutions.</span>
          </motion.h2>
          <p style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)', maxWidth: 500, marginTop: 16, lineHeight: 1.75 }}>
            Everything you need for your sacred journey — arranged with expertise, care, and the warmth of Ethiopian hospitality.
          </p>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger(0.08)}
            className="services-grid"
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1, background: 'rgba(201,168,76,0.2)', marginTop: 60,
            }}>
            {SERVICES.map((s) => {
              const Icon = iconMap[s.icon] || Plane;
              return (
                <motion.div key={s.id} variants={bloom} className="svc-card" style={{
                  background: '#0F2040', padding: '40px 32px', position: 'relative',
                  overflow: 'hidden', transition: 'all 0.3s',
                }}>
                  <div className="arabic-text" style={{
                    position: 'absolute', top: 16, right: 20, fontFamily: 'Amiri', fontWeight: 700,
                    color: 'rgba(201,168,76,0.1)', fontSize: 64,
                  }}>{s.id}</div>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#1B6B3A', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(201,168,76,0.4)' }}>
                    <Icon size={24} color="#C9A84C" strokeWidth={1.5} />
                  </div>
                  <div style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 20, marginTop: 22 }}>
                    {s.name}
                  </div>
                  <div className="arabic-text" style={{ fontStyle: 'italic', color: '#C9A84C', fontSize: 14, marginTop: 4 }}>
                    {s.arabic}
                  </div>
                  <div style={{ fontFamily: 'Inter', fontWeight: 600, color: '#C9A84C', fontSize: 10, marginTop: 8, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                    {s.tag}
                  </div>
                  <div style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 14, lineHeight: 1.8, marginTop: 14 }}>
                    {s.desc}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* GALLERY */}
      <section style={{ display: 'flex', height: 360, overflow: 'hidden' }} className="gallery-strip">
        {GALLERY_IMAGES.map((src, i) => (
          <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={imageReveal} custom={i}
            style={{ flex: 1, overflow: 'hidden' }}>
            <img src={src} alt="" style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transition: 'transform 0.6s ease',
            }} onMouseEnter={(e) => (e.target.style.transform = 'scale(1.08)')}
               onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')} />
          </motion.div>
        ))}
      </section>

      {/* TESTIMONIALS */}
      <section style={{ background: '#0A1628', padding: 'var(--section) 0', position: 'relative', overflow: 'hidden' }}>
        <GeometricPattern color="#C9A84C" opacity={0.04} size={100} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 'var(--container)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CrescentMoon size={36} color="#C9A84C" />
            </div>
            <div style={{ fontFamily: 'Inter', fontWeight: 500, color: '#C9A84C', fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', marginTop: 12 }}>
              — Testimonials —
            </div>
            <h2 style={{ fontFamily: 'Amiri', fontWeight: 700, color: '#FBF8F0', fontSize: 'var(--h2)', marginTop: 12 }}>
              From Our Pilgrims.
            </h2>
            <div className="arabic-text" style={{ fontStyle: 'italic', color: 'rgba(251,248,240,0.6)', fontSize: 18, marginTop: 8 }}>
              من حجاجنا ومعتمرينا
            </div>
          </div>

          <div className="test-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {testimonials.map((t, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sacredRise} custom={i}
                style={{
                  background: '#0F2040', borderTop: '2px solid #1B6B3A',
                  padding: '36px 28px', position: 'relative',
                }}>
                <div className="arabic-text" style={{
                  position: 'absolute', top: 8, right: 20, fontFamily: 'Amiri',
                  color: 'rgba(201,168,76,0.2)', fontSize: 64, lineHeight: 1,
                }}>❝</div>
                <p style={{ fontFamily: 'Amiri', fontStyle: 'italic', color: '#FBF8F0', fontSize: 16, lineHeight: 1.85 }}>
                  {t.quote}
                </p>
                <div style={{ width: 40, height: 1, background: '#C9A84C', margin: '20px 0' }} />
                <div style={{ display: 'flex', gap: 2, color: '#C9A84C', marginBottom: 8 }}>
                  {[...Array(5)].map((_, idx) => <Star key={idx} size={14} fill="#C9A84C" />)}
                </div>
                <div style={{ fontFamily: 'Inter', fontWeight: 600, color: '#FBF8F0', fontSize: 14 }}>{t.name}</div>
                <div style={{ fontFamily: 'Inter', color: '#C9A84C', fontSize: 12, marginTop: 2 }}>{t.package}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ServiceChunkBlock serviceIds={['01', '02', '06']} />

      {/* FINAL CTA */}
      <section style={{ position: 'relative', overflow: 'hidden', minHeight: 500, display: 'flex', alignItems: 'center' }}>
        <img src={CTA_IMAGE} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,22,40,0.85)' }} />
        <GeometricPattern color="#C9A84C" opacity={0.05} size={120} />
        <div style={{
          position: 'relative', zIndex: 2, maxWidth: 800, margin: '0 auto',
          padding: '80px var(--gutter)', textAlign: 'center', width: '100%',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CrescentMoon size={48} color="#C9A84C" />
          </div>
          <h2 style={{
            fontFamily: 'Amiri', fontWeight: 700, color: '#FFFFFF',
            fontSize: 'var(--h1)', marginTop: 20, lineHeight: 1.15,
          }}>
            Begin Your Sacred Journey.
          </h2>
          <div className="arabic-text" style={{ fontStyle: 'italic', color: 'rgba(201,168,76,0.9)', fontSize: 22, marginTop: 12 }}>
            ابدأ رحلتك المقدسة اليوم
          </div>
          <p style={{ fontFamily: 'Inter', color: '#FBF8F0', fontSize: 'var(--body-lg)', marginTop: 20 }}>
            We are available 24 hours a day. Call or WhatsApp us anytime, Inshallah we'll plan a journey to remember.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginTop: 40 }}>
            <Link to="/packages" style={{ textDecoration: 'none' }}>
              <button className="gold-pulse" style={{
                fontFamily: 'Amiri', fontWeight: 700, fontSize: 16, padding: '15px 36px',
                background: '#C9A84C', color: '#0A1628', border: 'none', cursor: 'pointer',
                letterSpacing: '0.04em',
              }}>
                View All Packages
              </button>
            </Link>
            <a href={`tel:${COMPANY.phoneIntl}`} style={{ textDecoration: 'none' }}>
              <button style={{
                fontFamily: 'Inter', fontWeight: 500, fontSize: 14, padding: '15px 28px',
                background: 'transparent', color: '#FBF8F0', border: '2px solid rgba(251,248,240,0.5)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Phone size={16} /> Call Now: {COMPANY.phoneDisplay}
              </button>
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .pkg-card:hover .pkg-img { transform: scale(1.08); }
        .pkg-card:hover { box-shadow: 0 20px 50px rgba(10,22,40,0.12); transform: translateY(-4px); }
        .svc-card:hover { background: #162D55 !important; }
        .svc-card:hover .icon-circle { background: #C9A84C !important; }
        @media (max-width: 1023px) {
          .package-grid, .services-grid, .test-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .gallery-strip { height: 260px !important; }
        }
        @media (max-width: 640px) {
          .package-grid, .services-grid, .test-grid {
            grid-template-columns: 1fr !important;
          }
          .stat-item { border-right: none !important; }
        }
      `}</style>
    </PageWrapper>
  );
}
